import { Routes } from '@packages/enums';
import type { AssistantMessageContent } from '@packages/types';
import { useAppSelector } from '@ui/app/store';
import { Icon } from '@ui/assets/icons/IconWrapper';
import DataTable from '@ui/components/common/DataTable';
import { useGetChatQuery } from '@ui/features/Chat/services/api';
import { selectIsSystemAdmin } from '@ui/store/slices/userSlice';
import { Tooltip } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import ClinicalTrialInfoPopover from './ClinicalTrialInfoPopover';
import PatientDetailsModal from './PatientDetailsModal';
import PatientDocumentList from './PatientDocumentList';
import PatientFilterPanelWrapper from './PatientFilterPanelWrapper';
import PatientListFilterGroup from './PatientListFilterGroup';
import { useCohortContext, useCohortForm, useCohortPatientData } from './state';
import Styles from './Styles.module.scss';
import type { IPatient } from './types';
import useDataTableProps from './useDataTableProps';

// Re-export types for backward compatibility
export type { ICareSite, IPagination, IPatient, IPatientRecord, IPatientRecordMetadata } from './types';

interface PatientListSelectionProps {
    /** Patient data from query */
    data: IPatient[];
    /** Total patient count */
    total?: number;
    /** Total unfiltered patient count */
    totalUnfiltered?: number;
    /** Loading state */
    loading?: boolean;
    /** Lazy loading state */
    isLazyLoadingPatienList?: boolean;
    /** Custom row selection configuration */
    rowSelection?: any;
}

export default function PatientListSelection({
    data,
    total,
    totalUnfiltered,
    loading,
    isLazyLoadingPatienList,
    rowSelection,
}: PatientListSelectionProps) {
    // Get state from constate hooks
    const { form } = useCohortForm();
    const {
        filter,
        setFilter,
        setPagination,
        paginationWithTotal: pagination,
        handleSearch: onSearch,
        handleOrderChange: onOrderChange,
    } = useCohortPatientData();
    const { messageId, clinicalTrialJobId, clinicalTrialDescription, clinicalTrialJobData, conversationData } =
        useCohortContext();

    const navigate = useNavigate();
    const isAdmin = useAppSelector(selectIsSystemAdmin);
    const [selectedPatientDetail, setSelectedPatientDetail] = useState<IPatient | undefined>(undefined);

    // Get chat data for SQL query
    const { data: chatData } = useGetChatQuery(
        { conversationId: conversationData?.conversation_id || '' },
        { skip: !conversationData?.conversation_id || !isAdmin }
    );

    // Extract description from conversation (messageId flow) or clinical trial (clinicalTrial flow)
    const description = useMemo(() => {
        // Clinical trial flow - use the passed description or fallback
        if (clinicalTrialJobId) {
            return clinicalTrialDescription || 'Clinical Trial Criteria';
        }
        // Copilot flow - use conversation data
        if (!messageId || !conversationData) return null;
        return (conversationData?.message as any)?.content?.assistant?.content?.summary_substring || null;
    }, [messageId, conversationData, clinicalTrialJobId, clinicalTrialDescription]);

    // Extract SQL query from messages (only for admin)
    const sqlQuery = useMemo(() => {
        if (!isAdmin || !chatData?.messages?.length) return null;

        const lastFinal = [...chatData.messages].reverse().find(msg => {
            const content: string | AssistantMessageContent = msg?.content?.content;
            const isFinal =
                typeof content !== 'string' &&
                (content?.is_final === true ||
                    content?.stage === 'final_response' ||
                    (typeof content?.text === 'string' && content.text.trim() !== ''));
            if (!isFinal) return false;

            const hasSql =
                typeof content?.sql_query === 'string' ||
                (Array.isArray(content?.sql_queries) && content.sql_queries?.[0]?.query);
            return hasSql;
        });

        if (!lastFinal) return null;

        const payload = lastFinal.content.content;
        const raw_query: string =
            (typeof payload !== 'string' &&
                (payload?.sql_query || (Array.isArray(payload?.sql_queries) && payload.sql_queries?.[0]?.query))) ||
            '';

        return raw_query;
    }, [isAdmin, chatData]);

    // Update pagination total when it changes; only reset current page if it would be out of bounds (e.g. was on page 21, now only 3 pages)
    useEffect(() => {
        if (total === undefined) return;
        setPagination(prev => {
            const pageSize = prev.pageSize || 10;
            const totalPages = Math.max(1, Math.ceil(total / pageSize));
            const currentPage = prev.current ?? 1;
            const current = currentPage > totalPages ? totalPages : currentPage;
            return { ...prev, total, current };
        });
    }, [total, setPagination]);

    const { columns } = useDataTableProps(data, undefined, undefined, form);

    // Keep server payload as-is to avoid shrinking page counts after fetch
    const filteredPatients = useMemo(() => (Array.isArray(data) ? data : []), [data]);

    return (
        <div className="flex gap-3">
            <div className="w-full rounded-[12px] bg-white p-5">
                <div className="flex flex-col gap-5">
                    {/* Patient List Header */}
                    <PatientListFilterGroup
                        title={
                            <div className="flex items-center gap-2">
                                <span className="text-[16px] font-semibold text-[#434343]">Patient List</span>
                                {description && (
                                    <div className="flex items-center gap-1">
                                        {clinicalTrialJobData ? (
                                            <ClinicalTrialInfoPopover jobData={clinicalTrialJobData}>
                                                <span className="cursor-pointer text-[14px] font-normal text-[#656565] hover:text-[#1677ff] hover:underline">
                                                    - {description}
                                                </span>
                                            </ClinicalTrialInfoPopover>
                                        ) : (
                                            <Tooltip
                                                title={
                                                    isAdmin && sqlQuery ? (
                                                        <div className="max-w-[600px]">
                                                            <pre className="m-0 whitespace-pre-wrap break-words font-mono text-[12px] text-white">
                                                                {sqlQuery}
                                                            </pre>
                                                        </div>
                                                    ) : null
                                                }
                                                color="#1f1f1f"
                                                overlayStyle={{ maxWidth: '600px' }}
                                            >
                                                <span className="cursor-pointer text-[14px] font-normal text-[#656565]">
                                                    - {description}
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                                {messageId && (
                                    <Tooltip
                                        title={
                                            <div className="w-max-content text-[12px] text-[#4D4D4D]">
                                                <div>Can&apos;t find the patients you&apos;re looking for?</div>
                                                <div>
                                                    <span
                                                        className="cursor-pointer font-semibold hover:underline"
                                                        onClick={() => navigate(`${Routes.CHAT}`)}
                                                    >
                                                        Start new chat
                                                    </span>
                                                    {' or '}
                                                    <span
                                                        className="cursor-pointer font-semibold hover:underline"
                                                        onClick={() => navigate(`${Routes.COHORTS}`)}
                                                    >
                                                        create cohort manually
                                                    </span>
                                                    .
                                                </div>
                                            </div>
                                        }
                                        color="white"
                                        styles={{ body: { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' } }}
                                    >
                                        <span className="flex cursor-pointer items-center">
                                            <Icon name="QuestionMark" />
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        }
                        filter={filter}
                        setFilter={setFilter}
                        onSearch={onSearch}
                        onOrderChange={onOrderChange}
                        isSearchEnabled={false}
                    />

                    {/* DataTable */}
                    <div className="min-w-0 flex-1">
                        <DataTable
                            className={Styles.DataTable}
                            dataSource={filteredPatients}
                            rowKey={row => String(row.id)}
                            tableLayout="fixed"
                            pagination={{
                                ...pagination,
                                onShowSizeChange: (_current, _size) => {},
                                onChange: (page, pageSize) => {
                                    setPagination({ ...pagination, current: page, pageSize });
                                },
                            }}
                            scroll={{ x: 1000, y: 'calc(100vh - 470px)' }}
                            size="small"
                            columns={columns as any}
                            loading={loading || isLazyLoadingPatienList}
                            rowSelection={rowSelection}
                            onRow={(record: IPatient) => {
                                return {
                                    onClick: () => {
                                        setSelectedPatientDetail(record);
                                    },
                                };
                            }}
                            expandable={{
                                expandedRowClassName: () => Styles.documentsRow,
                                rowExpandable: (record: IPatient) => (record.records?.length || 0) > 0,
                                expandedRowRender: (patient: IPatient) => (
                                    <PatientDocumentList
                                        showMatchedColumn={false}
                                        showWhereMatchedColumn={
                                            form?.getFieldValue('includeElasticsearchForFilters') === true
                                        }
                                        patient={patient}
                                    />
                                ),
                            }}
                        />
                    </div>
                </div>
            </div>

            <div className="flex w-[33%] flex-col gap-3">
                <div className="flex flex-col gap-3 rounded-[12px] bg-white p-3">
                    <span className="text-[16px] font-semibold text-[#656565]">Overview</span>
                    <div className="flex w-full">
                        <div className="flex w-[50%] flex-col">
                            <span className="text-[12px] font-medium leading-[20px] text-[#6B6B6B]">
                                Total Patients
                            </span>
                            <span className="text-md font-semibold text-[##2E2E2E]">{totalUnfiltered ?? total}</span>
                        </div>
                        <div className="flex w-[50%] flex-col">
                            <span className="text-[12px] font-medium leading-[20px] text-[#6B6B6B]">
                                Filtered Patients
                            </span>
                            <div className="flex items-center gap-2">
                                <span className="text-md font-semibold text-[##2E2E2E]">{total ?? 0}</span>
                                {messageId && (
                                    <Tooltip
                                        title={
                                            <div className="w-max-content text-[12px] text-[#4D4D4D]">
                                                These patients already meet the criteria you defined earlier in the
                                                chat. Any additional filters will be applied on top of those existing
                                                criteria.
                                            </div>
                                        }
                                        color="white"
                                        styles={{ body: { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)' } }}
                                    >
                                        <span className="flex cursor-pointer items-center [&_svg_path]:stroke-[#00B2FF]">
                                            <Icon name="NonCategorized" />
                                        </span>
                                    </Tooltip>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <PatientFilterPanelWrapper
                    hidden={false}
                    messageId={messageId}
                />
            </div>

            {/* Patient Details Modal */}
            <PatientDetailsModal
                open={!!selectedPatientDetail}
                onClose={() => setSelectedPatientDetail(undefined)}
                patient={selectedPatientDetail}
            />
        </div>
    );
}
